/**
 * Administração › Canais: vínculos da equipe e destinatários de alertas — Nexora Grid (frontend, US-2103).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * O administrador da operadora confere se o plantão recebe os alarmes: quem está vinculado ao Telegram/WhatsApp, desde
 * quando e o último aviso entregue; desfaz um vínculo (ex.: funcionário desligado — vincular continua sendo pelo próprio
 * usuário) e mantém os e-mails e webhooks das notificações das regras e dos incidentes (`gerenciar_canais`).
 * US-3101: coluna "Celular" — aparelho com passkey, prazo para cadastrar ou "parado" (sem resposta e sem alertas).
 * US-3104: tabela "Celulares cadastrados" (ativos e histórico de revogados, com motivo); o administrador revoga com
 * motivo (`gerenciar_canais`). A revogação automática (usuário desativado, senha trocada, sem uso) aparece aqui também.
 * US-3304: celular SUSPENSO pela Sentinela aparece com o motivo e o botão Reativar (com motivo).
 * v2.0 (US-4604, Sprint 46): painel de estado do guia (§4.4) — KPIs do plantão (vinculados, recebem alertas, parados,
 *   celulares ativos, suspensos, destinatários ativos), blocos com cabeçalho, estado com forma e texto, idade do último
 *   aviso e do último uso, e a ação na mesma linha do estado que ela resolve. Mesmas chamadas e as mesmas regras.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, ErroApi } from "../api";
import { CabecalhoTela, EstadoAlarme, EstadoVazio, Esqueleto, FaixaKpis, Selo } from "../componentes/Tela";
import { idadeRelativa } from "../componentes/regrasTela";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { AbasAdmin } from "./AbasAdmin";
import { quando } from "./tipos";
import "./admin-b.css";

type Vinculo = { canal: string; conta: string; nome_conta: string; vinculado_em: string; receber_alertas: boolean; login: string; nome: string;
  usuario_ativo: boolean; ultimo_aviso_em: string | null; liberado?: boolean; aparelho?: string | null; aparelho_ate?: string | null };
type Aparelho = { id: string; canal: string; conta: string; nome_aparelho: string; estado: string; criado_em: string; ultimo_uso_em: string | null;
  expira_em: string | null; vencido: boolean; revogado_em: string | null; motivo: string; login: string; nome: string };
type Destinatario = { id: string; tipo: "email" | "webhook"; destino: string; descricao: string; ativo: boolean; criado_por: string };

const nomeCanal = (c: string) => (c === "telegram" ? "Telegram" : "WhatsApp");

/** Data com a idade relativa ao lado ("há 3 h"); a data completa fica no título. */
function Quando({ iso, vazio = "—" }: { iso: string | null | undefined; vazio?: string }) {
  if (!iso) return <span className="nx-suave">{vazio}</span>;
  return <span title={quando(iso)}>{quando(iso)} <small className="nx-suave">({idadeRelativa(iso)})</small></span>;
}

function Bloco({ titulo, texto, children, contagem }: { titulo: string; texto: ReactNode; children: ReactNode; contagem?: ReactNode }) {
  return (
    <section className="tl-bloco">
      <div className="tl-bloco-cab"><h3>{titulo}</h3>{contagem}<span className="espaco" /></div>
      <div className="tl-bloco-corpo"><p className="tl-texto">{texto}</p></div>
      <div className="tl-rolagem">{children}</div>
    </section>
  );
}

export function Canais() {
  const { usuario, empresa } = useSessao();
  const [vinculos, setVinculos] = useState<Vinculo[] | null>(null);
  const [destinos, setDestinos] = useState<Destinatario[]>([]);
  const [aparelhos, setAparelhos] = useState<Aparelho[]>([]);
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [novo, setNovo] = useState({ tipo: "email" as Destinatario["tipo"], destino: "", descricao: "" });
  const carregar = useCallback(() => {
    if (!empresa) return;
    api<Vinculo[]>(`/v1/admin/canais/vinculos?empresa=${empresa}`).then(setVinculos).catch(() => setVinculos([]));
    api<Destinatario[]>(`/v1/admin/canais/destinatarios?empresa=${empresa}`).then(setDestinos).catch(() => setDestinos([]));
    api<Aparelho[]>(`/v1/admin/canais/aparelhos?empresa=${empresa}`).then(setAparelhos).catch(() => setAparelhos([]));
  }, [empresa]);
  useEffect(carregar, [carregar]);
  if (!usuario || !empresa) return null;
  if (!pode.verUsuarios(usuario)) return <p className="nx-suave">Sem acesso à administração dos canais.</p>;
  const gerencia = pode.gerenciarCanais(usuario);
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); setAviso(ok); carregar(); }
    catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };
  const cadastrar = (e: FormEvent) => {
    e.preventDefault();
    void executar(() => api("/v1/admin/canais/destinatarios", { corpo: { empresa, ...novo } }), "Destinatário cadastrado: o motor passa a usá-lo em até 1 minuto.")
      .then(() => setNovo({ ...novo, destino: "", descricao: "" }));
  };
  const lista = vinculos ?? [];
  const parados = lista.filter((v) => !v.aparelho && !v.liberado).length;
  const ativos = aparelhos.filter((a) => a.estado === "ativo" && !a.vencido).length;
  const suspensos = aparelhos.filter((a) => a.estado === "suspenso").length;
  const vencidos = aparelhos.filter((a) => a.estado === "ativo" && a.vencido).length;

  return (
    <section className="tl-pagina admin-b">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[{ texto: NOMES.administracao }, { texto: NOMES.canais }]} titulo={NOMES.canais}
        subtitulo={usuario.nomes_empresas?.[empresa] ?? empresa} />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      {vinculos === null ? <Esqueleto linhas={6} /> : (
        <FaixaKpis itens={[
          { chave: "vinculos", rotulo: "Vinculados", valor: lista.length, detalhe: "Telegram e WhatsApp" },
          { chave: "alertas", rotulo: "Recebem alertas", valor: lista.filter((v) => v.receber_alertas && v.usuario_ativo).length, detalhe: "do plantão" },
          { chave: "parados", rotulo: "Parados", valor: parados, detalhe: "sem celular cadastrado", tom: parados ? "semcom" : undefined },
          { chave: "celulares", rotulo: "Celulares ativos", valor: ativos, detalhe: vencidos ? `${vencidos} vencido(s)` : "com passkey" },
          { chave: "suspensos", rotulo: "Suspensos", valor: suspensos, detalhe: "pela Sentinela", tom: suspensos ? "alarme" : undefined },
          { chave: "destinos", rotulo: "Destinatários", valor: destinos.filter((d) => d.ativo).length, detalhe: `de ${destinos.length} cadastrado(s)` },
        ]} />
      )}

      <Bloco titulo="Vínculos da equipe (Telegram e WhatsApp)" contagem={<Selo tom="suave">{lista.length}</Selo>}
        texto="Cada pessoa vincula o próprio celular pelo código mostrado no perfil. Aqui você confere quem recebe os alertas e desfaz um vínculo quando precisar.">
        <table className="tl-tabela" aria-label="Vínculos">
          <thead><tr><th scope="col">Pessoa</th><th scope="col">Canal</th><th scope="col">Vinculado em</th><th scope="col">Celular</th><th scope="col">Recebe alertas</th>
            <th scope="col">Último aviso entregue</th>{gerencia && <th scope="col"><span className="visualmente-oculto">Ação</span></th>}</tr></thead>
          <tbody>
            {lista.map((v) => (
              <tr key={`${v.canal}/${v.conta}`}>
                <td className="tl-quebra">{v.nome} <small className="nx-suave">{v.login}{!v.usuario_ativo && " · conta desativada"}</small></td>
                <td>{nomeCanal(v.canal)} <small className="nx-suave">{v.nome_conta}</small></td>
                <td><Quando iso={v.vinculado_em} /></td>
                <td>{v.aparelho ? <Selo tom="ok"><span aria-hidden>🔐</span> {v.aparelho}</Selo>
                  : v.liberado ? <Selo tom="atencao">cadastrar até {v.aparelho_ate ? quando(v.aparelho_ate) : "—"}</Selo>
                  : <EstadoAlarme estado="semcom" texto="parado: sem celular cadastrado" />}</td>
                <td>{v.receber_alertas ? "sim" : <span className="nx-suave">não</span>}</td>
                <td><Quando iso={v.ultimo_aviso_em} vazio="nenhum" /></td>
                {gerencia && <td><button type="button" className="nx-botao" onClick={() => void executar(
                  () => api(`/v1/admin/canais/vinculos/${v.canal}/${encodeURIComponent(v.conta)}?empresa=${empresa}`, { metodo: "DELETE" }),
                  `Vínculo de ${v.nome} desfeito.`)}>Desfazer vínculo</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!lista.length && <EstadoVazio titulo="Ninguém vinculado" texto="Peça à equipe do plantão para vincular o celular pelo código do perfil." />}
      </Bloco>

      <Bloco titulo="Celulares cadastrados (passkey)" contagem={<Selo tom="suave">{aparelhos.length}</Selo>}
        texto="Cada celular confirma as ações do chat com a biometria. Revogue o de quem perdeu o aparelho ou saiu da equipe — o chat dessa pessoa para até ela cadastrar outro. A revogação é automática quando a pessoa é desativada, troca a senha ou fica sem usar o chat.">
        <table className="tl-tabela" aria-label="Celulares">
          <thead><tr><th scope="col">Pessoa</th><th scope="col">Celular</th><th scope="col">Situação</th><th scope="col">Último uso</th><th scope="col">Vence</th>
            {gerencia && <th scope="col">Ação (com motivo)</th>}</tr></thead>
          <tbody>
            {aparelhos.map((a) => (
              <tr key={a.id}>
                <td className="tl-quebra">{a.nome} <small className="nx-suave">{a.login}</small></td>
                <td><span aria-hidden>🔐</span> {a.nome_aparelho} <small className="nx-suave">{nomeCanal(a.canal)} {a.conta}</small></td>
                <td className="tl-quebra">{a.estado === "ativo" ? (a.vencido ? <Selo tom="atencao">vencido (aguarda renovação)</Selo> : <Selo tom="ok">ativo</Selo>)
                  : a.estado === "suspenso" ? <EstadoAlarme estado="medio" texto={`suspenso — ${a.motivo}`} />
                  : <Selo tom="suave">revogado {a.revogado_em ? quando(a.revogado_em) : ""} — {a.motivo}</Selo>}</td>
                <td><Quando iso={a.ultimo_uso_em} /></td>
                <td>{a.expira_em && a.estado === "ativo" ? quando(a.expira_em) : <span className="nx-suave">—</span>}</td>
                {gerencia && <td>{(a.estado === "ativo" || a.estado === "suspenso") && (
                  <span className="reconhecer">
                    <input aria-label={`motivo para revogar ${a.nome_aparelho} de ${a.nome}`} placeholder="motivo (ex.: celular perdido)" maxLength={200}
                      value={motivos[a.id] ?? ""} onChange={(e) => setMotivos({ ...motivos, [a.id]: e.target.value })} />
                    <button type="button" className="nx-botao" disabled={(motivos[a.id] ?? "").trim().length < 5} onClick={() => void executar(
                      () => api(`/v1/admin/canais/aparelhos/${a.id}/revogar`, { corpo: { empresa, motivo: motivos[a.id] } }),
                      `Celular de ${a.nome} revogado.`)}>Revogar</button>
                    {a.estado === "suspenso" && <button type="button" className="nx-botao" disabled={(motivos[a.id] ?? "").trim().length < 5}
                      onClick={() => void executar(() => api(`/v1/admin/canais/aparelhos/${a.id}/reativar`, { corpo: { empresa, motivo: motivos[a.id] } }),
                        `Celular de ${a.nome} reativado.`)}>Reativar</button>}
                  </span>)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!aparelhos.length && <EstadoVazio titulo="Nenhum celular cadastrado" />}
      </Bloco>

      <Bloco titulo="Destinatários das notificações (e-mail e webhook)" contagem={<Selo tom="suave">{destinos.length}</Selo>}
        texto="Recebem as notificações das regras e dos incidentes desta operadora. Sem destinatário cadastrado, vale a configuração padrão do ambiente.">
        <table className="tl-tabela" aria-label="Destinatários">
          <thead><tr><th scope="col">Tipo</th><th scope="col">Destino</th><th scope="col">Descrição</th><th scope="col">Situação</th>
            {gerencia && <th scope="col"><span className="visualmente-oculto">Ações</span></th>}</tr></thead>
          <tbody>
            {destinos.map((d) => (
              <tr key={d.id}>
                <td>{d.tipo === "email" ? "E-mail" : "Webhook"}</td><td className="tl-mono tl-quebra">{d.destino}</td><td className="tl-quebra">{d.descricao || "—"}</td>
                <td><Selo tom={d.ativo ? "ok" : "suave"}>{d.ativo ? "Ativo" : "Desativado"}</Selo></td>
                {gerencia && <td><span className="acoes-linha">
                  <button type="button" className="nx-botao" onClick={() => void executar(() => api(`/v1/admin/canais/destinatarios/${d.id}/${d.ativo ? "desativar" : "ativar"}`, { corpo: {} }),
                    d.ativo ? "Destinatário desativado." : "Destinatário ativado.")}>{d.ativo ? "Desativar" : "Ativar"}</button>
                  <button type="button" className="nx-botao" onClick={() => void executar(() => api(`/v1/admin/canais/destinatarios/${d.id}`, { metodo: "DELETE" }), "Destinatário excluído.")}>Excluir</button>
                </span></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!destinos.length && <EstadoVazio titulo="Nenhum destinatário cadastrado" texto="Vale o padrão do ambiente." />}
        {gerencia && (
          <form className="form-filial tl-bloco-corpo" onSubmit={cadastrar} aria-label="Novo destinatário" style={{ borderTop: "1px solid var(--borda)" }}>
            <div className="nx-campo"><label htmlFor="cn-tipo">Tipo</label>
              <select id="cn-tipo" value={novo.tipo} onChange={(e) => setNovo({ ...novo, tipo: e.target.value as Destinatario["tipo"] })}>
                <option value="email">E-mail</option><option value="webhook">Webhook (https)</option></select></div>
            <div className="nx-campo"><label htmlFor="cn-destino">{novo.tipo === "email" ? "E-mail" : "URL do webhook"}</label>
              <input id="cn-destino" required type={novo.tipo === "email" ? "email" : "url"} value={novo.destino} onChange={(e) => setNovo({ ...novo, destino: e.target.value.trim() })} /></div>
            <div className="nx-campo"><label htmlFor="cn-descricao">Descrição</label>
              <input id="cn-descricao" maxLength={120} value={novo.descricao} placeholder="ex.: plantão da engenharia" onChange={(e) => setNovo({ ...novo, descricao: e.target.value })} /></div>
            <button type="submit" className="nx-botao nx-botao-principal">Cadastrar destinatário</button>
          </form>
        )}
      </Bloco>
    </section>
  );
}
